import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import styled from "styled-components";
import { Theme } from "../atoms/theme";
import { SubtitlesM } from "../atoms/Typography";
import { NoteCard } from "../molecules/NoteCard";
import { Note } from "../../types/Note";
import useToast from "../../hooks/useToast";
import { usePendingNotes } from "./PendingNotesContext";
import {
  SearchIcon,
  XIcon,
  CheckIcon,
  ChevronUpIcon,
  ClipboardCopyIcon,
} from "@heroicons/react/outline";

/* ── Styled ── */

const Container = styled.div<{ embedded?: boolean }>`
  margin-bottom: ${({ embedded }) => (embedded ? "0" : "1rem")};
  background: ${({ embedded }) =>
    embedded ? "transparent" : Theme.colors.neutrals_500};
  border: ${({ embedded }) =>
    embedded ? "none" : `1px solid ${Theme.colors.neutrals_300}`};
  border-top: ${({ embedded }) =>
    embedded
      ? `1px solid ${Theme.colors.neutrals_300}`
      : `1px solid ${Theme.colors.neutrals_300}`};
  border-radius: ${({ embedded }) => (embedded ? "0" : "6px")};
  padding: 0.75rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  gap: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Count = styled.span`
  font-size: 12px;
  color: ${Theme.colors.gray_200};
  font-weight: 500;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinkBtn = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  color: ${Theme.colors.primaries_000};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 0;
  &:hover {
    color: ${Theme.colors.primaries_neg100};
  }
`;

const SearchIconBtn = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: ${Theme.colors.gray_200};
  display: flex;
  align-items: center;
  &:hover {
    color: ${Theme.colors.primaries_000};
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
`;

const SearchInput = styled.input`
  flex: 1;
  border: 1px solid ${Theme.colors.neutrals_300};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  &:focus {
    outline: none;
    border-color: ${Theme.colors.primaries_100};
  }
`;

const ExpandedPanel = styled.div<{ embedded?: boolean }>`
  max-height: ${({ embedded }) => (embedded ? "250px" : "450px")};
  overflow-y: auto;
  padding-top: 0.25rem;
`;

const FooterAction = styled.div`
  text-align: center;
  padding: 0.5rem 0 0.25rem;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: ${Theme.colors.gray_200};
  text-align: center;
  padding: 0.75rem 0;
`;

const SearchCount = styled.div`
  font-size: 11px;
  color: ${Theme.colors.gray_200};
  padding: 0.5rem 0 0;
`;

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0;
  margin: 0.25rem 0;
`;

const SectionLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${Theme.colors.gray_200};
  white-space: nowrap;
`;

const SectionLine = styled.hr`
  flex: 1;
  border: none;
  border-top: 1px solid ${Theme.colors.neutrals_300};
  margin: 0;
`;

/* ── Inline add ── */

const InlineAddWrap = styled.div`
  padding: 0.5rem 0;
`;

const InlineTextarea = styled.textarea`
  width: 100%;
  min-height: 52px;
  padding: 0.4rem;
  border: 1px solid ${Theme.colors.neutrals_300};
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: ${Theme.colors.primaries_000};
    box-shadow: 0 0 0 2px ${Theme.colors.primaries_300};
  }
`;

const InlineActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
  justify-content: flex-end;
`;

const InlineBtn = styled.button.attrs({ type: "button" })<{
  primary?: boolean;
}>`
  background: ${({ primary }) =>
    primary ? Theme.colors.primaries_000 : "none"};
  color: ${({ primary }) => (primary ? "#fff" : Theme.colors.gray_200)};
  border: ${({ primary }) =>
    primary ? "none" : `1px solid ${Theme.colors.neutrals_300}`};
  cursor: pointer;
  padding: 3px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ── Constants ── */

const PREVIEW_COUNT = 3;

/* ── Component ── */

export interface NotesListProps {
  notes: Note[];
  isLoading: boolean;
  isCreating: boolean;
  title?: string;
  readOnly?: boolean;
  embedded?: boolean;
  defaultExpanded?: boolean;
  clientMode?: boolean;
  currentUserId?: string;
  onCreateNote: (body: string) => Promise<void>;
  onUpdateNote: (
    noteId: string,
    fields: { body?: string; isHighlighted?: boolean; isBanned?: boolean }
  ) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
}

export const NotesList = ({
  notes,
  isLoading,
  isCreating,
  title,
  readOnly = false,
  embedded = false,
  defaultExpanded = false,
  clientMode = false,
  currentUserId,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
}: NotesListProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();
  const { register: registerFlush, unregister: unregisterFlush } =
    usePendingNotes();
  const notesListId = useRef(
    `notes-${Math.random().toString(36).slice(2, 9)}`
  ).current;

  // Register pending add content so the page-level save can flush it
  const addValueRef = useRef(addValue);
  addValueRef.current = addValue;
  const showAddRef = useRef(showAdd);
  showAddRef.current = showAdd;

  const flushPendingAdd = useCallback(async () => {
    if (showAddRef.current && addValueRef.current.trim()) {
      try {
        await onCreateNote(addValueRef.current.trim());
        setAddValue("");
        setShowAdd(false);
      } catch {
        // Swallow — toast will show from the main save flow
      }
    }
  }, [onCreateNote]);

  useEffect(() => {
    if (showAdd && addValue.trim()) {
      registerFlush(notesListId, flushPendingAdd);
    } else {
      unregisterFlush(notesListId);
    }
    return () => unregisterFlush(notesListId);
  }, [
    showAdd,
    addValue,
    notesListId,
    registerFlush,
    unregisterFlush,
    flushPendingAdd,
  ]);

  const displayTitle = title || "Notes";

  // Filter notes by search term
  const filteredNotes = useMemo(() => {
    if (!searchTerm.trim()) return notes;
    const q = searchTerm.toLowerCase();
    return notes.filter(
      (n) =>
        n.body.toLowerCase().includes(q) ||
        (n.createdByName || "").toLowerCase().includes(q)
    );
  }, [notes, searchTerm]);

  // Partition notes into: critical (highlighted) pinned at top, then firm, then team.
  // Within each group: normal first, then banned. All preserve API date order (DESC).
  const { criticalNotes, clientNotes, raynaNotes } = useMemo(() => {
    const critical: Note[] = [];
    const client: Note[] = [];
    const clientBanned: Note[] = [];
    const rayna: Note[] = [];
    const raynaBanned: Note[] = [];
    for (const n of filteredNotes) {
      if (n.isHighlighted) {
        critical.push(n);
      } else if (n.createdByRole === "client") {
        if (n.isBanned) clientBanned.push(n);
        else client.push(n);
      } else {
        if (n.isBanned) raynaBanned.push(n);
        else rayna.push(n);
      }
    }
    return {
      criticalNotes: critical,
      clientNotes: [...client, ...clientBanned],
      raynaNotes: [...rayna, ...raynaBanned],
    };
  }, [filteredNotes]);

  // Ordered notes: critical first, then firm, then team
  const orderedNotes = useMemo(
    () => [...criticalNotes, ...clientNotes, ...raynaNotes],
    [criticalNotes, clientNotes, raynaNotes]
  );

  // What to show in collapsed vs expanded
  const displayedNotes = expanded
    ? orderedNotes
    : orderedNotes.slice(0, PREVIEW_COUNT);
  const hasMore = notes.length > PREVIEW_COUNT;

  useEffect(() => {
    if (searching && searchRef.current) searchRef.current.focus();
  }, [searching]);

  useEffect(() => {
    if (showAdd && addRef.current) addRef.current.focus();
  }, [showAdd]);

  // Reset search & selection when collapsing
  const collapse = () => {
    setExpanded(false);
    setSearching(false);
    setSearchTerm("");
    setSelectedIds(new Set());
  };

  const openExpanded = () => {
    setExpanded(true);
  };

  const toggleSearch = () => {
    if (searching) {
      setSearching(false);
      setSearchTerm("");
    } else {
      setSearching(true);
    }
  };

  const handleSelect = (noteId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(noteId);
      else next.delete(noteId);
      return next;
    });
  };

  const formatNoteMeta = (n: Note) => {
    const d = new Date(n.createdAt);
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${n.createdByName || "Unknown"} · ${date}, ${time}`;
  };

  const handleCopySelected = () => {
    const selectedNotes = orderedNotes.filter((n) => selectedIds.has(n.id));
    if (selectedNotes.length === 0) return;
    const text = selectedNotes
      .map((n) => `${formatNoteMeta(n)}\n${n.body}`)
      .join("\n\n---\n\n");
    navigator.clipboard.writeText(text);
    toast.success(
      `Copied ${selectedNotes.length} note${
        selectedNotes.length > 1 ? "s" : ""
      }`
    );
    setSelectedIds(new Set());
  };

  /* ── Handlers ── */

  const handleCreate = async () => {
    if (!addValue.trim()) return;
    try {
      await onCreateNote(addValue.trim());
      toast.success("Note posted");
      setAddValue("");
      setShowAdd(false);
    } catch {
      toast.error("Failed to add note");
    }
  };

  const handleSaveEdit = async (noteId: string, body: string) => {
    try {
      await onUpdateNote(noteId, { body });
      toast.success("Note updated");
    } catch {
      toast.error("Failed to update note");
    }
  };

  const handleToggleHighlight = async (note: Note) => {
    try {
      await onUpdateNote(note.id, { isHighlighted: !note.isHighlighted });
    } catch {
      toast.error("Failed to update note");
    }
  };

  const handleToggleBan = async (note: Note) => {
    try {
      await onUpdateNote(note.id, { isBanned: !note.isBanned });
    } catch {
      toast.error("Failed to update note");
    }
  };

  const handleDelete = async (note: Note) => {
    if (!onDeleteNote) return;
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await onDeleteNote(note.id);
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  /* ── Loading ── */

  if (isLoading) {
    return (
      <Container embedded={embedded}>
        <Header>
          <SubtitlesM color={Theme.colors.neutrals_000}>
            {displayTitle}
          </SubtitlesM>
        </Header>
        <EmptyText>Loading...</EmptyText>
      </Container>
    );
  }

  /* ── Render ── */

  return (
    <Container embedded={embedded}>
      <Header>
        {searching ? (
          <SearchBar>
            <SearchInput
              ref={searchRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search notes..."
              onKeyDown={(e) => {
                if (e.key === "Escape") toggleSearch();
              }}
            />
            <SearchIconBtn onClick={toggleSearch} title="Close search">
              <XIcon width={14} />
            </SearchIconBtn>
          </SearchBar>
        ) : (
          <HeaderLeft
            onClick={() => (expanded ? collapse() : openExpanded())}
            style={{ cursor: "pointer" }}
          >
            <ChevronUpIcon
              width={14}
              style={{
                transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.15s",
                color: Theme.colors.gray_200,
              }}
            />
            <SubtitlesM color={Theme.colors.neutrals_000}>
              {displayTitle}
            </SubtitlesM>
            {notes.length > 0 && <Count>({notes.length})</Count>}
          </HeaderLeft>
        )}
        <HeaderRight>
          {expanded && !readOnly && !clientMode && selectedIds.size > 0 && (
            <LinkBtn onClick={handleCopySelected} title="Copy selected notes">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <ClipboardCopyIcon width={13} /> Copy ({selectedIds.size})
              </span>
            </LinkBtn>
          )}
          {expanded && !searching && (
            <SearchIconBtn onClick={toggleSearch} title="Search notes">
              <SearchIcon width={15} />
            </SearchIconBtn>
          )}
          {(!readOnly || clientMode) && (
            <LinkBtn
              onClick={() => {
                setShowAdd(true);
                setAddValue("");
                if (!expanded) openExpanded();
              }}
            >
              + Add
            </LinkBtn>
          )}
        </HeaderRight>
      </Header>

      {/* ── Collapsed: compact previews ── */}
      {!expanded && notes.length > 0 && (
        <>
          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            {displayedNotes.map((note, i) => (
              <NoteCard
                key={note.id}
                note={note}
                compact
                readOnly={readOnly}
                isClientNote={note.createdByRole === "client"}
                showDivider={i < displayedNotes.length - 1}
              />
            ))}
          </div>
          {hasMore && (
            <FooterAction>
              <LinkBtn onClick={openExpanded}>
                Show all {notes.length} notes
              </LinkBtn>
            </FooterAction>
          )}
        </>
      )}

      {/* ── Expanded: scrollable panel with full notes ── */}
      {expanded && (
        <ExpandedPanel embedded={embedded}>
          {showAdd && (!readOnly || clientMode) && (
            <InlineAddWrap>
              <InlineTextarea
                ref={addRef}
                value={addValue}
                onChange={(e) => setAddValue(e.target.value)}
                placeholder="Type your note..."
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setShowAdd(false);
                    setAddValue("");
                  }
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                    handleCreate();
                }}
              />
              <InlineActions>
                <InlineBtn
                  onClick={() => {
                    setShowAdd(false);
                    setAddValue("");
                  }}
                >
                  Cancel
                </InlineBtn>
                {clientMode && (
                  <InlineBtn
                    primary
                    disabled={!addValue.trim() || isCreating}
                    onClick={handleCreate}
                  >
                    <CheckIcon width={12} />{" "}
                    {isCreating ? "Posting..." : "Post"}
                  </InlineBtn>
                )}
              </InlineActions>
            </InlineAddWrap>
          )}

          {searching && searchTerm && (
            <SearchCount>
              {filteredNotes.length} result
              {filteredNotes.length !== 1 ? "s" : ""} for &ldquo;{searchTerm}
              &rdquo;
            </SearchCount>
          )}

          {orderedNotes.length === 0 ? (
            <EmptyText>
              {searchTerm ? "No notes match your search" : "No notes yet"}
            </EmptyText>
          ) : (
            <>
              {/* Critical / highlighted notes — pinned at top */}
              {criticalNotes.length > 0 && (
                <>
                  <SectionDivider>
                    <SectionLine />
                    <SectionLabel style={{ color: Theme.colors.warning_000 }}>
                      Critical
                    </SectionLabel>
                    <SectionLine />
                  </SectionDivider>
                  {criticalNotes.map((note, i) => {
                    const isClient = note.createdByRole === "client";
                    const isOwnNote =
                      currentUserId && note.createdById === currentUserId;
                    const isOwnClientNote = clientMode && isOwnNote;
                    return (
                      <NoteCard
                        key={note.id}
                        note={note}
                        isClientNote={isClient}
                        clientMode={clientMode}
                        selected={selectedIds.has(note.id)}
                        onSelect={
                          readOnly || clientMode ? undefined : handleSelect
                        }
                        onSaveEdit={
                          clientMode
                            ? isOwnClientNote
                              ? handleSaveEdit
                              : undefined
                            : readOnly
                            ? undefined
                            : handleSaveEdit
                        }
                        onToggleHighlight={
                          readOnly || clientMode
                            ? undefined
                            : handleToggleHighlight
                        }
                        onToggleBan={
                          readOnly || clientMode ? undefined : handleToggleBan
                        }
                        onDelete={
                          onDeleteNote && isOwnNote && !readOnly
                            ? handleDelete
                            : undefined
                        }
                        showDivider={i < criticalNotes.length - 1}
                        readOnly={readOnly && !clientMode}
                      />
                    );
                  })}
                </>
              )}

              {/* Firm / client notes */}
              {clientNotes.length > 0 && (
                <>
                  {criticalNotes.length > 0 && (
                    <SectionDivider>
                      <SectionLine />
                      <SectionLabel>Firm Notes</SectionLabel>
                      <SectionLine />
                    </SectionDivider>
                  )}
                  {clientNotes.map((note, i) => {
                    const isOwnNote =
                      currentUserId && note.createdById === currentUserId;
                    const isOwnClientNote = clientMode && isOwnNote;
                    return (
                      <NoteCard
                        key={note.id}
                        note={note}
                        isClientNote
                        clientMode={clientMode}
                        selected={selectedIds.has(note.id)}
                        onSelect={
                          readOnly || clientMode ? undefined : handleSelect
                        }
                        onSaveEdit={
                          clientMode
                            ? isOwnClientNote
                              ? handleSaveEdit
                              : undefined
                            : readOnly
                            ? undefined
                            : handleSaveEdit
                        }
                        onToggleHighlight={
                          readOnly || clientMode
                            ? undefined
                            : handleToggleHighlight
                        }
                        onToggleBan={
                          readOnly || clientMode ? undefined : handleToggleBan
                        }
                        onDelete={
                          onDeleteNote && isOwnNote && !readOnly
                            ? handleDelete
                            : undefined
                        }
                        showDivider={i < clientNotes.length - 1}
                        readOnly={readOnly && !clientMode}
                      />
                    );
                  })}
                </>
              )}

              {/* Team / rayna notes */}
              {raynaNotes.length > 0 && (
                <>
                  {(criticalNotes.length > 0 || clientNotes.length > 0) && (
                    <SectionDivider>
                      <SectionLine />
                      <SectionLabel>Rayna Notes</SectionLabel>
                      <SectionLine />
                    </SectionDivider>
                  )}
                  {raynaNotes.map((note, i) => {
                    const isOwnNote =
                      currentUserId && note.createdById === currentUserId;
                    return (
                      <NoteCard
                        key={note.id}
                        note={note}
                        clientMode={clientMode}
                        selected={selectedIds.has(note.id)}
                        onSelect={
                          readOnly || clientMode ? undefined : handleSelect
                        }
                        onSaveEdit={
                          readOnly || clientMode ? undefined : handleSaveEdit
                        }
                        onToggleHighlight={
                          readOnly || clientMode
                            ? undefined
                            : handleToggleHighlight
                        }
                        onToggleBan={
                          readOnly || clientMode ? undefined : handleToggleBan
                        }
                        onDelete={
                          onDeleteNote && isOwnNote && !readOnly
                            ? handleDelete
                            : undefined
                        }
                        showDivider={i < raynaNotes.length - 1}
                        readOnly={readOnly || clientMode}
                      />
                    );
                  })}
                </>
              )}
            </>
          )}
        </ExpandedPanel>
      )}
    </Container>
  );
};
